import { getTranslation } from '@owc/aem-translations-util';
import { createHttpClient } from '../bundle';
import { LOG_LEVEL } from '../types/enums';
import type { Translations } from '../types/translation-type';
import { logger } from '../utilities/logger';
import { getAemConfig } from '../utilities/seamless';

const PHRASE_BASE_URL = 'https://i18n.oneweb.mercedes-benz.com/projects';

const defaultCountry = 'DE';
const defaultLanguage = 'en';

// default locale in Phrase is en-DE
const defaultLocale = `${defaultLanguage}-${defaultCountry}`;

let translations = {
  locale: defaultLocale,
  messages: {},
} as Translations;

const storageLocalesKey = 'locales';
const storagePrefix = 'translations';
const storageLocalesTimestampKey = 'localesTimestamp';
const localesStorageDuration = 24 * 60 * 60 * 1000; // one day

function getByPath<T extends Record<string, any>, Path extends string>(obj: T, path: Path): string {
  return (path.split('.').reduce((acc, key) => acc?.[key], obj) as unknown as string) || 'No translation';
}

export function useTranslations() {
  const { locale, messages } = translations;
  const translatedMessages = messages[locale] || {};

  return {
    exist: (path: string) => getByPath(translatedMessages, path) !== 'No translation',
    t: (path: string) => getByPath(translatedMessages, path),
    availableLocales: [locale],
    locale,
  };
}

export async function loadTranslation(projectId: string) {
  const { country = defaultCountry, language = defaultLanguage } = await getAemConfig();
  let locale = `${language}-${country}`;

  let isSupported = await isLocaleSupported(locale, projectId);

  if (!isSupported) {
    locale = defaultLocale;

    // check also if default locale is supported
    isSupported = await isLocaleSupported(locale, projectId);

    if (!isSupported) {
      logger(
        `Neither ${locale} nor the default locale ${defaultLocale} is supported. No translations will be loaded.`,
        {
          args: [],
          level: LOG_LEVEL.INFO,
          group: 'TRANSLATIONS',
        },
      );
      return translations;
    }

    logger(`Locale ${locale} is not supported, falling back to default locale ${defaultLocale}.`, {
      args: [],
      level: LOG_LEVEL.INFO,
      group: 'TRANSLATIONS',
    });
  }

  try {
    translations = {
      locale,
      messages: {
        [locale]: await getTranslation(locale, projectId, 'i18next'),
      },
    };
  } catch (error) {
    logger(`Error fetching translations for ${locale}.`, {
      args: [error],
      level: LOG_LEVEL.INFO,
      group: 'TRANSLATIONS',
    });
  }

  return translations;
}

const isLocaleSupported = async (locale: string, projectId: string): Promise<boolean> => {
  const availableLocales = await getSupportedLocales(projectId);
  return availableLocales.includes(locale);
};

const getSupportedLocales = async (projectId: string) => {
  const httpClient = createHttpClient({ prefixUrl: `${PHRASE_BASE_URL}/${projectId}` });

  const storedLocalesStorageKey = `${storagePrefix}.${projectId}.${storageLocalesKey}`;
  const storedLocalesStorageTimestampKey = `${storagePrefix}.${projectId}.${storageLocalesTimestampKey}`;

  const storedLocales = localStorage.getItem(storedLocalesStorageKey);
  const storedTimestamp = localStorage.getItem(storedLocalesStorageTimestampKey);

  if (storedLocales && storedTimestamp) {
    const lastFetched = new Date(storedTimestamp);
    const now = new Date();

    if (now.getTime() - lastFetched.getTime() < localesStorageDuration) {
      return JSON.parse(storedLocales);
    }
  }

  try {
    const response = await httpClient.get('locales');

    const i18nLocales = ((await response.json()) as Array<{ name: string }>).map((locale) => locale.name);

    if (!i18nLocales?.length) {
      logger('Error fetching i18n locales', {
        args: ['Reason:', 'Locales require to be fetched after a timeout'],
        group: 'TRANSLATIONS',
        level: LOG_LEVEL.INFO,
      });
      return [];
    }

    localStorage.setItem(storedLocalesStorageKey, JSON.stringify(i18nLocales));
    localStorage.setItem(storedLocalesStorageTimestampKey, new Date().toISOString());

    return i18nLocales;
  } catch (error) {
    logger('Error fetching i18n locales', {
      args: [error],
      group: 'TRANSLATIONS',
      level: LOG_LEVEL.ERROR,
    });

    return [];
  }
};
